import { OptionWithIdType } from '~/features/types';
import { MedicalBoard, SpecBoard, SpecCert, SubSpecCert } from '~/types';
import { Specialty } from '~/types/common/training';
import { MEDICAL_BOARD_API_CALLS } from './medicalBoard/medicalBoard-service';
import { SPECIALTY_API_CALLS } from './specialty/specialty-service';
import { SPEC_BOARD_API_CALLS } from './specialtyBoard/specialtyBoard-service';
import { SPECIALTY_CERT_API_CALLS } from './specialtyCertificate/specialtyCertificate-service';
import { SUB_SPECIALTY_CERT_API_CALLS } from './subSpecialtyCertificate/subSpecialtyCertificate-service';

export const medicalBoardResponseToOptions = (data: string): OptionWithIdType[] => {
  const boards: MedicalBoard[] = JSON.parse(data);
  return boards.map((board: MedicalBoard) => ({
    label: board.boardName,
    value: board.boardCode,
    id: board.id.toString(),
  }));
};

export const specialtyBoardResponseToOptions = (data: string): OptionWithIdType[] => {
  const specialtyBoards: SpecBoard[] = JSON.parse(data);
  return specialtyBoards.map((specBoard) => ({
    label: specBoard.name,
    value: specBoard.specialtyBoardCode,
    id: specBoard.id.toString(),
  }));
};

export const specialtyResponseToOptions = (data: string): OptionWithIdType[] => {
  const specialties: Specialty[] = JSON.parse(data);
  return specialties.map((spec: Specialty) => ({
    label: spec.specialtyName,
    value: spec.specialtyBoardCode,
    id: spec.id.toString(),
  }));
};

export const specialtyCertsResponseToOptions = (data: string): OptionWithIdType[] => {
  const certs: [] = JSON.parse(data);
  return certs.map((cert: SpecCert) => ({
    label: cert.certification,
    value: cert.specialtyBoardCode,
    id: cert.id.toString(),
  }));
};

export const subSpecialtyCertsResponseToOptions = (data: string): OptionWithIdType[] => {
  const certs: [] = JSON.parse(data);
  return certs.map((cert: SubSpecCert) => ({
    label: cert.subspecialtyCertificationName,
    value: cert.subspecialtyCertificationName,
    id: cert.id.toString(),
  }));
};

export const loadMedicalBoardOptions = async (
  providerId: number,
  providerCategoryId: number
): Promise<OptionWithIdType[]> => {
  return await MEDICAL_BOARD_API_CALLS.get({
    input: { context: { providerId, providerCategoryId } },
    transformResponse: medicalBoardResponseToOptions,
  });
};

export const loadSpecBoardOptions = async (medicalBoardId: string): Promise<OptionWithIdType[]> => {
  return await SPEC_BOARD_API_CALLS.get({
    medicalBoardId,
    transformResponse: specialtyBoardResponseToOptions,
  });
};

export const loadSpecialtyOptions = async (
  specialtyBoardId: string
): Promise<OptionWithIdType[]> => {
  return await SPECIALTY_API_CALLS.get({
    specialtyBoardId,
    transformResponse: specialtyResponseToOptions,
  });
};

export const loadSpecCertOptions = async (specId: string): Promise<OptionWithIdType[]> => {
  const options = await SPECIALTY_CERT_API_CALLS.get({
    specId,
    transformResponse: specialtyCertsResponseToOptions,
  });
  return options.map((option) => ({ ...option, value: option.id }));
};

export const loadSpecCertOptionsBySpecialty = async (
  specialtyId: string
): Promise<OptionWithIdType[]> => {
  const options = await SPECIALTY_CERT_API_CALLS.getBySpecialty({
    specialtyId,
    transformResponse: specialtyCertsResponseToOptions,
  });
  return options.map((option) => ({ ...option, value: option.id }));
};

export const loadSubSpecCertOptions = async (
  specialtyBoardId: string
): Promise<OptionWithIdType[]> => {
  return await SUB_SPECIALTY_CERT_API_CALLS.get({
    specialtyBoardId,
    transformResponse: subSpecialtyCertsResponseToOptions,
  });
};

export const loadSubSpecCertOptionsByCertification = async (
  specialtyCertificationId: string
): Promise<OptionWithIdType[]> => {
  const options = await SUB_SPECIALTY_CERT_API_CALLS.getByCertification({
    specialtyCertificationId,
    transformResponse: subSpecialtyCertsResponseToOptions,
  });
  return options.map((option) => ({ ...option, value: option.id }));
};
